import {
  BrandPrototypeData,
  cloneBrandPrototypeData,
} from "@/components/Settings/components/brandPrototypeModel";

export const BRAND_PROTOTYPE_STORAGE_KEY =
  "bustly.admin.brand-prototype.v10.workspace";
export const BRAND_STORAGE_MIGRATION_KEY =
  "bustly.admin.brand-prototype.v10-workspace-scoped";

const LEGACY_BRAND_STORAGE_KEYS = [
  "bustly.admin.brand-prototype.v6",
  "bustly.admin.brand-prototype.v7",
  "bustly.admin.brand-prototype.v8",
  "bustly.admin.brand-prototype.v8-migrated",
];
const LEGACY_BRAND_STORAGE_PREFIXES = [
  "bustly.admin.brand-prototype.v9.workspace.",
];

const isStoredPrototypeData = (value: unknown): value is BrandPrototypeData => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const candidate = value as Partial<BrandPrototypeData>;
  return (
    Array.isArray(candidate.brands) &&
    Array.isArray(candidate.connections) &&
    Array.isArray(candidate.users) &&
    Array.isArray(candidate.memberships) &&
    Array.isArray(candidate.connectionAccessGrants) &&
    Boolean(candidate.externalConnectionBrandIds) &&
    Boolean(candidate.externalGoogleAdsCustomerBrandIds) &&
    typeof candidate.activeAgentBrandId === "string"
  );
};

export const getBrandPrototypeStorageKey = (workspaceId: string) =>
  `${BRAND_PROTOTYPE_STORAGE_KEY}.${encodeURIComponent(workspaceId)}`;

export const clearUnscopedBrandPrototypeStorage = () => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    if (window.sessionStorage.getItem(BRAND_STORAGE_MIGRATION_KEY)) {
      return;
    }

    LEGACY_BRAND_STORAGE_KEYS.forEach((storageKey) =>
      window.sessionStorage.removeItem(storageKey),
    );
    const legacyWorkspaceKeys = Array.from(
      { length: window.sessionStorage.length },
      (_, index) => window.sessionStorage.key(index),
    ).filter(
      (storageKey): storageKey is string =>
        Boolean(storageKey) &&
        LEGACY_BRAND_STORAGE_PREFIXES.some((prefix) =>
          storageKey.startsWith(prefix),
        ),
    );
    legacyWorkspaceKeys.forEach((storageKey) =>
      window.sessionStorage.removeItem(storageKey),
    );
    window.sessionStorage.setItem(BRAND_STORAGE_MIGRATION_KEY, "true");
  } catch {
    // The Workspace-scoped in-memory state remains usable when storage fails.
  }
};

export const readWorkspaceBrandPrototypeData = (
  workspaceId?: string | null,
): BrandPrototypeData => {
  clearUnscopedBrandPrototypeStorage();

  if (typeof window === "undefined" || !workspaceId) {
    return cloneBrandPrototypeData();
  }

  const storageKey = getBrandPrototypeStorageKey(workspaceId);
  try {
    const storedValue = window.sessionStorage.getItem(storageKey);
    if (!storedValue) {
      return cloneBrandPrototypeData();
    }

    const parsedValue: unknown = JSON.parse(storedValue);
    if (!isStoredPrototypeData(parsedValue)) {
      window.sessionStorage.removeItem(storageKey);
      return cloneBrandPrototypeData();
    }

    return cloneBrandPrototypeData(parsedValue);
  } catch {
    return cloneBrandPrototypeData();
  }
};

export const writeWorkspaceBrandPrototypeData = (
  workspaceId: string,
  data: BrandPrototypeData,
) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(
      getBrandPrototypeStorageKey(workspaceId),
      JSON.stringify(data),
    );
  } catch {
    // The Workspace-scoped in-memory state remains usable when storage fails.
  }
};
